import { toast } from "@ui/molecules/Toasts/toast";
import { promptWithoutVideoThumbnails } from "@ui/organisms/QuestionBox/promptMentionSerialization";
import { getPreferredLocale } from "@utils/locale";
import { type CreateQuestionData, createQuestion } from "@/client";
import { useMeteringBalance } from "~/modules/shared/utils/useMeteringBalance";

type StartChatProps = CreateQuestionData["body"] & {
	clearQuestion?: () => void;
	/** Called instead of the default toast when the creator's AskPage needs a pass or subscription. */
	onEntitlementRequired?: () => void;
};

function errorReason(error: unknown): string | undefined {
	if (typeof error !== "object" || error === null) {
		return undefined;
	}
	const reason = (error as Record<string, unknown>).reason;
	return typeof reason === "string" ? reason : undefined;
}

export async function startChat(props: StartChatProps) {
	const { clearQuestion, onEntitlementRequired, ...rest } = props;
	if (rest.scope !== "project") {
		rest.project_id = undefined;
	}
	const { data, error, response } = await createQuestion({
		body: {
			...rest,
			prompt: promptWithoutVideoThumbnails(rest.prompt),
			preferred_locale: getPreferredLocale(),
		},
	});

	// A 402 is also returned for a paid creator without a pass, which credits
	// can't fix.
	if (
		response?.status === 402 &&
		errorReason(error) === "entitlement_required"
	) {
		if (onEntitlementRequired) {
			onEntitlementRequired();
			return;
		}
		toast.error({
			title: "You need a pass or subscription",
			description: "Get one on this creator's AskPage to ask them questions.",
			store: false,
		});
		return;
	}

	if (response?.status === 402) {
		toast.error({
			title: "You're out of question credits",
			description:
				"Top up or upgrade your plan to keep asking, or wait for your next reset.",
			store: false,
		});
		// The balance on the page was stale; refetching shows the banner and
		// disables the box.
		void useMeteringBalance().refetch();
		return;
	}

	const path = data?.path;
	if (error || !path) {
		console.error("startChat: question not created", error ?? data);
		toast.error({
			title: "Couldn't ask that question",
			description: "Something went wrong. Please try again.",
			store: false,
		});
		return;
	}

	clearQuestion?.();
	window.location.href = path;
}
